// App shell (docs/app.md §2). Three levels of navigation, one place each
// (owner, 2026-09-28): the SIDEBAR picks the section, grouped under the
// workspace's heading ("Invoice Processing"); the HEADER'S TABS pick the page
// within that section (`components/PageTabs.tsx`); and a one-line intro at the
// top of the body says what the page is for (`components/ScreenIntro.tsx`).
// On Invoices the header's tabs are the pipeline steps this person holds. The
// step timeline still lives in a modal dialog off the header's Pipeline button;
// its node count badges are the at-a-glance KPI, and the Analytics page holds
// the full metrics view. Routing is by role: only the nodes the user can act on
// are lit, and selecting one loads that screen. The API enforces access
// independently of what is shown, so lighting a node is a hint and never a
// permission.
//
// The workspace level is kept even though Invoices is the only workspace: the
// state, the label map and the per-workspace role projection are the mechanism a
// second document class rides on later. What is not kept is a one-option toggle.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import {
  Body1,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Hamburger,
  NavDrawer,
  NavDrawerBody,
  NavItem,
  NavSectionHeader,
  Tooltip,
  Toaster,
  makeStaticStyles,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AppDialog } from './components/AppDialog'
import {
  ArrowClockwiseRegular,
  ArrowUploadRegular,
  BoardRegular,
  BookDatabaseRegular,
  DismissRegular,
  InfoRegular,
  MailRegular,
  PeopleTeamRegular,
  ErrorCircleRegular,
  ReceiptRegular,
  AlertRegular,
  SettingsRegular,
  TimelineRegular,
} from '@fluentui/react-icons'
import {
  TIMELINE_NODES,
  TOASTER_ID,
  DecisionStep,
  InvoiceStatus,
  WORKSPACE_LABEL,
  WORKSPACE_NAV_HEADING,
  type NodeKey,
  type Role,
  type Workspace,
} from './lib/constants'
import { loadSession, scopeSession, availableWorkspaces, filterDecidable, goToNodeFor, hasRole, canOpenAssignments, canOpenAnalytics, canOpenErrors, canOpenReferences, canPreviewPdf, landingPage, visibleAnalyticsTabs, visibleReferenceTabs, visibleRuleTabs, type Session } from './lib/identity'
import { loadCompaniesForManagement, loadInvoicesByStatus, loadStuckNodes, type StuckNode } from './lib/data'
import { applyFilters, companyFilterOptions, emptyFilter, vendorFilterOptions, type InvoiceFilter } from './lib/filters'
import type { Invoice } from './lib/types'
import { PAGE_ID, TOPBAR_ID } from './lib/overlay'
import { useDraggableDialog } from './lib/hooks'
import { AUTO_REFRESH_MS, INITIAL_REFRESH, nextRefresh, shouldAutoRefresh, type RefreshSignal } from './lib/refresh'
import { Timeline } from './components/Timeline'
import { BrandMark } from './components/BrandMark'
import { PIPELINE_DIALOG_MAX_W } from './lib/timelineLayout'
import { guideUrlFor } from './lib/guides'
import { FilterButton } from './components/FilterButton'
import { ScreenActionsHost } from './components/ScreenActions'
import { PageTabs, PageTabsHost } from './components/PageTabs'
import { ImportControlScreen } from './screens/ImportControlScreen'
import { ApprovalScreen } from './screens/ApprovalScreen'
import { ExportControlScreen } from './screens/ExportControlScreen'
import { ClosingConfirmationScreen } from './screens/ClosingConfirmationScreen'
import { DenialReviewScreen } from './screens/DenialReviewScreen'
import { ArchivesScreen } from './screens/ArchivesScreen'
import { AssignmentsScreen } from './screens/AssignmentsScreen'
import { TrainingScreen } from './screens/TrainingScreen'
import { RulesScreen } from './screens/RulesScreen'
import { AnalyticsScreen } from './screens/AnalyticsScreen'
import { ErrorsScreen } from './screens/ErrorsScreen'
import { NotificationsScreen } from './screens/NotificationsScreen'
import { UploadScreen } from './screens/UploadScreen'
import { UploadProgressToast } from './components/UploadProgressToast'
import { RemindersScreen } from './screens/RemindersScreen'
import { APP_VERSION } from './version'

// The main-path + branch step roles (everything but the archive lookup).
const PIPELINE_ROLES: Role[] = ['ImportControl', 'ControllerApproval', 'DepartmentApproval', 'DirectorApproval', 'OversightApproval', 'ExportControl', 'ClosingConfirmation', 'DenialReview']

// Sidebar values. Analytics / References / Rules / Assignments map to
// their page keys; Invoices maps to the workspace.
type NavValue = 'invoices' | 'notifications' | 'analytics' | 'training' | 'rules' | 'assignments' | 'errors'

// The lit set drives which timeline nodes the user can open. Archives is a
// read-only view of terminal invoices reachable by anyone who works any
// pipeline step — Dataverse RLS scopes the rows (a company approver sees only
// their own company's history; internal roles see all), so lighting it broadly
// is safe. A dedicated Archives-team member (the 'Archives' role) lights it
// too. See questions.md → Archives (Option A) and app.md §7.
/** True on a pipeline queue — i.e. not one of the off-pipeline pages. Drives
    which header tools are shown, and whether the 30-second refresh runs. */
function isQueuePage(selected: string | null): boolean {
  return (
    selected !== 'assignments' &&
    selected !== 'training' &&
    selected !== 'rules' &&
    selected !== 'dashboard' &&
    selected !== 'errors' &&
    selected !== 'notifications'
  )
}

function litRolesFor(session: Session): Set<Role> {
  // No fail-open branch: the profile either loaded or the shell showed an error,
  // so an empty role set means the user really holds no step.
  const base: Set<Role> = new Set(session.roles)
  if (PIPELINE_ROLES.some((r) => base.has(r))) base.add('Archives')
  return base
}

// Fluent styles TableHeaderCell's inner button with `resize: horizontal`, which
// paints a native drag grip in the corner of every column header. It is there
// for Fluent's own column-sizing feature (useTableColumnSizing) — which this app
// doesn't use anywhere, and whose column widths are fixed in code. So the grip
// resized only the header's inner div: drag it and the label clipped to
// "DESCRIP" while the column stayed put (user note 2026-08-02). Turn it off
// everywhere rather than per table, so a new table can't reintroduce it.
// The doubled class is deliberate: Fluent's own `resize: horizontal` is an
// atomic single-class rule in the same style bucket, so at equal specificity it
// wins on source order. Doubling makes this rule more specific instead.
//
// `body` loses the browser's default 8px margin, so the sidebar's column runs to
// the window's left edge instead of stopping 8px short of it.
const useGlobalTableStyles = makeStaticStyles({
  '.fui-TableHeaderCell__button.fui-TableHeaderCell__button': { resize: 'none' },
  body: { margin: 0 },
})

// Where the sidebar's open/closed choice is remembered, per browser. A
// convenience, never a setting: an unreadable store just means "open".
const NAV_COLLAPSED_KEY = 'ap.nav.collapsed'
function readNavCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}
function writeNavCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch {
    /* private window or blocked storage: the choice lasts this visit only */
  }
}

const useStyles = makeStyles({
  // The whole window: the header across the top, then the sidebar and the page
  // side by side. `minHeight` lets the sidebar's column reach the bottom of the
  // window on a short page.
  shell: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  body: { display: 'flex', flex: '1 0 auto', alignItems: 'stretch' },
  // THE SIDEBAR GLIDES, AND THE PAGE GLIDES WITH IT (owner, 2026-09-28: "the
  // content kind of quickly snaps into place … make the pane and the content
  // kind of glide smoothly in tandem"). Fluent's inline drawer animated
  // itself and then dropped out of the layout in one step, so the page jumped
  // to its new width at the end. Now the drawer stays open inside this frame
  // and the FRAME's width is what moves: the page is `flex: 1` beside it, so it
  // resizes on every frame of the same transition rather than after it.
  //
  // Closed, the frame is also `visibility: hidden` — delayed until the width
  // has finished — so the links leave the tab order and the screen reader's
  // view without cutting the slide short. Opening shows them at once.
  // Reduced-motion settings get the change without the slide.
  sidebarFrame: {
    display: 'flex',
    flexShrink: 0,
    width: '240px',
    overflow: 'hidden',
    transition: `width ${tokens.durationNormal} ${tokens.curveEasyEase}, visibility 0s`,
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  sidebarFrameClosed: {
    width: '0px',
    visibility: 'hidden',
    transition: `width ${tokens.durationNormal} ${tokens.curveEasyEase}, visibility 0s ${tokens.durationNormal}`,
  },
  // Fixed at full width inside the frame, so its contents never re-wrap as the
  // frame narrows; the frame's `overflow: hidden` crops them instead.
  //
  // `maxHeight: 'none'` IS LOAD-BEARING (owner, 2026-09-29, D-203: the pane
  // "does not seem to run to the bottom of the screen"). Fluent's drawer base
  // style caps the drawer at `max-height: 100vh`, so on any page taller than
  // the window the pane stopped one window-height down and the page ran on
  // below it. Uncapped, it stretches with the body to the page's full height.
  sidebar: {
    width: '240px',
    flexShrink: 0,
    maxHeight: 'none',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // CENTRES EACH SIDEBAR ICON ON ITS LABEL (owner, 2026-10-07: "check that the
  // icons in the navigation sidebar are properly centered with their
  // associated text"). Fluent's NavItem draws the icon in a 20px grid cell
  // with `align-items: top` - an invalid value, so the glyph falls to the top
  // of the cell - and expects a 20px glyph that fills it. These icons are the
  // unsized kind, 1em of the 14px body text, so they sat at the top of the
  // cell: measured 2.5px above the label's centre on every item. Centring the
  // glyph in the cell puts it on the label's line without changing its size.
  navIcon: { alignItems: 'center' },
  // The page column: everything right of the sidebar. NO WIDTH LIMIT (owner,
  // 2026-09-28: "Closing the navigation pane does not stretch the width of the
  // content; they just move over"). It kept the old centred page's 1240px cap
  // for one round, so hiding the sidebar slid the same-width page left instead
  // of giving it the room. The detail modal's overlay measures this box
  // (`lib/overlay.ts`), so it follows the width too.
  page: { flex: '1 1 0', minWidth: 0, padding: '24px 28px 64px', boxSizing: 'border-box' },
  errorPage: { maxWidth: '1240px', margin: '0 auto', padding: '20px 28px 64px' },
  // Quiet enough to ignore, findable when someone is reporting a problem.
  versionLine: {
    marginTop: '24px',
    textAlign: 'right',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    userSelect: 'text',
  },
  // Full window width, above the sidebar and the page alike. It no longer
  // wraps: the page tabs fold into their own overflow menu instead
  // (`PageTabs.tsx`), so the tools always keep their row.
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '8px 28px 8px 12px',
    // ONE HEIGHT ON EVERY SCREEN, page tab or none (owner, 2026-10-07: "The
    // bar at the top of the screen does not appear to be the same height as
    // the same bar on all other screens."). The tab row sets it at 63px; a
    // section with no tab (Notifications since 2026-10-19) would drop to 51px.
    boxSizing: 'border-box',
    minHeight: '63px',
    // Blue accent line that separates the header from the page body.
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  // The organization's own butterfly mark (owner, 2026-09-01), replacing BOTH
  // the receipt glyph that used to sit left of the wordmark and the brand-blue
  // outline tried between them. The receipt duplicated the Invoices tab's own
  // icon further along the row; the outline avoided that but read as a control.
  //
  // IT SITS TO THE RIGHT OF THE WORDMARK, as the SEPARATOR between the app's
  // name and the navigation (owner, same day). So it is a direct child of
  // `navZone` rather than part of a brand lockup — that way the 20px nav gap
  // falls on BOTH sides of it and the spacing is even by construction, instead
  // of a lockup gap on one side and the nav gap on the other.
  brandLogo: { display: 'block', flexShrink: 0 },
  // The app title as a wordmark (app.md §7): small, full-contrast text. The app
  // name is static chrome, so it stays small and each page's title is the
  // page's only large heading — but full contrast, not muted (a faint gray
  // first cut read as faded, revised same day).
  wordmark: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  // Left zone: the sidebar toggle, the brand wordmark, the mark, then the page
  // tabs, which take whatever width is left.
  navZone: { display: 'flex', alignItems: 'center', gap: '20px', minWidth: 0, flex: '1 1 0' },
  // The toggle sits tight against the wordmark: it belongs to the app's own
  // chrome, not to the tabs the mark separates it from.
  hamburger: { marginRight: '-12px' },
  // Right zone: the same-screen tools as compact icon buttons — deliberately
  // distinct from the labeled page tabs (header redesign 2026-07-14).
  // WIDER THAN FLUENT'S DEFAULT ICON BUTTON (owner, 2026-08-14: "slightly widen
  // the buttons in the top-right corner"). A medium Fluent `Button` with only an
  // icon collapses to a 32px square; 44px gives each glyph visible shoulders
  // without turning the cluster into labelled buttons, which the page tabs
  // already are. The descendant selector covers `FilterButton` too — it renders
  // its own Fluent `Button` inside this container and must not be the one narrow
  // control in the row.
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
    '& button': { minWidth: '44px' },
  },
  // Breathing room around the timeline inside the Pipeline dialog.
  // `overflowX: 'auto'`: below ~1066px of viewport the timeline's fixed-width
  // nodes cannot compress (`lib/timelineLayout.ts`'s `timelineMinWidth`), so
  // below that floor the STRIP scrolls instead of the whole dialog body — the
  // title, the close button and the muted explanation line stay put. This
  // also makes `overflow-y` compute from `visible` to `auto` (CSS Overflow 3:
  // one axis non-`visible` forces the other to `auto`) — the same rule that
  // caused the original horizontal-scroll bug — but it cannot bite here: this
  // box's height is `auto`, so it grows to its content, and at NODE_H 84 no
  // label ever spills out of its button, so there is nothing to overflow
  // vertically in the first place.
  pipelineBody: { padding: '12px 4px 8px', overflowX: 'auto' },
  pipelineSub: { display: 'block', color: tokens.colorNeutralForeground3, marginTop: '4px' },
  loading: { display: 'flex', justifyContent: 'center', padding: '96px' },
  noRole: { padding: '48px 0', color: tokens.colorNeutralForeground3 },
})

function firstLitNode(litRoles: Set<Role>): NodeKey | null {
  for (const n of TIMELINE_NODES) {
    if (litRoles.has(n.role)) return n.key
  }
  return null
}

export default function App() {
  useGlobalTableStyles()
  const s = useStyles()
  const [session, setSession] = useState<Session | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [selected, setSelected] = useState<NodeKey | 'dashboard' | 'training' | 'rules' | 'assignments' | 'errors' | 'notifications' | null>(null)
  // Minimal rows per timeline node; badge counts derive from these so the
  // shared queue filter applies to the whole page (counts + queues together).
  const [nodeRows, setNodeRows] = useState<Record<string, Invoice[]>>({})
  // One filter shared by every step queue and the timeline badges, so filtering
  // to a company narrows the badges and the queues together.
  const [filter, setFilter] = useState<InvoiceFilter>(emptyFilter)
  // Active workspace. A user's roles are per-workspace; the scoped session
  // drives which timeline nodes are lit.
  const [workspace, setWorkspace] = useState<Workspace>('invoice')
  const litRef = useRef<Set<Role>>(new Set())
  // Last timeline node the user worked, so Processing returns them to it.
  const lastNodeRef = useRef<NodeKey | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  // The sidebar: open unless this browser was last told to hide it.
  const [navOpen, setNavOpen] = useState(() => !readNavCollapsed())
  const toggleNav = () =>
    setNavOpen((open) => {
      writeNavCollapsed(open)
      return !open
    })
  const dragUpload = useDraggableDialog(uploadOpen, 'Upload')
  // Reminders: self-service email-reminder subscriptions in a modal (reminders.md).
  const [remindersOpen, setRemindersOpen] = useState(false)
  // Custom Notifications on the list; Add or Edit Custom Notification on the form (D-454).
  const [remindersTitle, setRemindersTitle] = useState('Custom Notifications')
  // Pipeline: shows the step timeline in a modal dialog (Upload's pattern).
  // Selecting a node closes it and loads that step's queue.
  const [pipelineOpen, setPipelineOpen] = useState(false)
  // THE ONE REFRESH SIGNAL (D-160, `lib/refresh.ts`). Every bump refreshes the
  // timeline counts AND every queue gallery together, so the two are always in
  // step. The Refresh button bumps it NOT SILENT (spinner allowed); the
  // Pipeline button and the 30-second timer bump it SILENT.
  const [refresh, setRefresh] = useState<RefreshSignal>(INITIAL_REFRESH)
  // Bumped by the Refresh button alone. Analytics and the stuck-tier read take
  // this rather than `refresh`, so the timer can never re-run a Dashboard query
  // or `GET /queues/stuck` every 30 seconds.
  const [manualNonce, setManualNonce] = useState(0)
  // The Pipeline dialog's warning-icon diagnostic (§2.2 `GET /queues/stuck`).
  // Fetched only while the dialog is open — never on the timer, which is why
  // this lives beside `pipelineOpen` rather than `refreshStats`.
  const [stuck, setStuck] = useState<Partial<Record<string, StuckNode>>>({})

  // ---- session ----
  useEffect(() => {
    loadSession()
      .then((sess) => {
        setSession(sess)
        const wss = availableWorkspaces(sess)
        const initialWs: Workspace = wss.includes('invoice') || wss.length === 0 ? 'invoice' : wss[0]
        setWorkspace(initialWs)
        const lit = litRolesFor(scopeSession(sess, initialWs))
        litRef.current = lit
        const first = firstLitNode(lit)
        setSelected(first ?? landingPage(sess))
      })
      .catch((e: Error) => setSessionError(e.message))
  }, [])

  // Session scoped to the active workspace (roles/teamIds point at that
  // workspace's teams) — drives lit nodes and the invoice screens' canDecide.
  const scoped = useMemo<Session | null>(() => (session ? scopeSession(session, workspace) : null), [session, workspace])
  const litRoles = useMemo<Set<Role>>(() => (scoped ? litRolesFor(scoped) : new Set()), [scoped])

  // ---- badge counts (lit nodes only) + light auto-refresh ----
  const refreshStats = useCallback(async () => {
    const lit = litRoles
    // Skip the archive node — its queue is unbounded (the screen paginates),
    // so it carries no waiting-count badge.
    const nodes = TIMELINE_NODES.filter((n) => lit.has(n.role) && !n.archive)
    const next: Record<string, Invoice[]> = {}
    await Promise.all(
      nodes.map(async (n) => {
        try {
          const rows = await loadInvoicesByStatus(n.queueStatuses)
          // NARROWED THE SAME WAY THE SCREEN BELOW NARROWS (QA walk,
          // 2026-08-30). Every step screen runs `filterDecidable` over its
          // queue — the owner's 2026-08-14 ruling that "a user cannot see or
          // act on any invoices outside his role" — and the badge did not, so
          // at the four company-scoped steps it promised rows that were not
          // there: Manager Approval read 16 against a queue of 12. `docs/app.md`
          // §2 already said what this should do ("Counts respect access — a
          // company approver's badge counts only their own company"); the code
          // was the side that was wrong. Applied HERE rather than in `counts`
          // so `allNodeRows` is narrowed too and the Filter button stops
          // offering a company whose rows this user can never see.
          // `app/mock/tests/timeline-badge-scope.test.ts` pins it.
          next[n.key] = scoped && n.step ? filterDecidable(scoped, n.step, rows) : rows
        } catch {
          /* leave the node's rows as-is on a transient error */
        }
      }),
    )
    setNodeRows((prev) => ({ ...prev, ...next }))
  }, [litRoles, scoped])

  // Counts recompute locally when the filter changes — no refetch needed. The
  // loaded rows have already been narrowed to what this user may act on
  // (`refreshStats` above), and run through the same page filter the queues
  // use, so a badge and its queue can never disagree.
  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const [key, rows] of Object.entries(nodeRows)) c[key] = applyFilters(rows, filter).length
    return c
  }, [nodeRows, filter])

  // All rows behind the badges — these feed the Filter button's options, which
  // is why the filter never offers the whole ~2,700-row vendor master.
  const allNodeRows = useMemo(() => Object.values(nodeRows).flat(), [nodeRows])
  // The company master's names, keyed by id, for the company filter's options
  // (code review D-14). Read once a session exists; a failure leaves the
  // invoice's own stored name standing in.
  const [companyNames, setCompanyNames] = useState<ReadonlyMap<string, string>>(() => new Map())
  useEffect(() => {
    if (!scoped) return
    let cancelled = false
    loadCompaniesForManagement()
      .then((cs) => {
        if (!cancelled) setCompanyNames(new Map(cs.map((c) => [c.company_id, c.company_name])))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [scoped])
  const filterCompanies = useMemo(() => companyFilterOptions(allNodeRows, companyNames), [allNodeRows, companyNames])
  const filterVendors = useMemo(
    () => vendorFilterOptions(allNodeRows),
    [allNodeRows],
  )

  // ONE REFRESH FOR THE COUNTS AND THE GALLERIES (owner, 2026-09-25: "The
  // gallery lists need to refresh in tandem with the counts, so they are always
  // in sync"). `refreshStats` re-reads the badge rows; the signal bump re-reads
  // whichever gallery is on screen. Silent or not, both move on the same call.
  //
  // `refreshStats` is silent by construction: it merges into `nodeRows` per node
  // and leaves a node's rows alone when its read throws, so no badge blanks,
  // nothing spins, and no number flickers to zero on the way.
  const refreshAll = useCallback(
    (opts?: { silent?: boolean }) => {
      const silent = !!opts?.silent
      void refreshStats()
      setRefresh((r) => nextRefresh(r, silent))
      if (!silent) setManualNonce((n) => n + 1)
    },
    [refreshStats],
  )

  // First counts as soon as the session and its lit nodes are known.
  useEffect(() => {
    if (!session) return
    void refreshStats()
  }, [session, refreshStats])

  // THE 30-SECOND REFRESH (owner, 2026-09-25: "it should not be more than
  // thirty-seconds-long … the screen should not flicker with the loading
  // spinner on such a timed refresh"). One interval, silent, counts and gallery
  // together. It replaced the 90-second badge-only poll rather than running
  // beside it.
  //
  // - Only on the Invoices pages, where the counts and galleries are. Analytics
  //   and Errors refresh by hand only; the off-pipeline pages have no gallery.
  //   Coming back to a queue page catches the counts up at once (below).
  // - Skipped while the browser tab is hidden, and caught up with one silent
  //   refresh when it becomes visible again, so a background tab costs nothing.
  // - The latest `refreshAll` and page are read through refs, so the interval
  //   is set up once per session and a render never resets its clock.
  const onQueuePageNow = isQueuePage(selected)
  const tickRef = useRef<() => void>(() => {})
  useEffect(() => {
    tickRef.current = () => {
      if (onQueuePageNow) refreshAll({ silent: true })
    }
  }, [onQueuePageNow, refreshAll])
  useEffect(() => {
    if (!session) return
    const tick = () => {
      if (shouldAutoRefresh(document.visibilityState)) tickRef.current()
    }
    const id = window.setInterval(tick, AUTO_REFRESH_MS)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [session])
  // Returning to the Invoices tab from an off-pipeline page: the timer did not
  // run there, so catch the counts up. The gallery loads itself on mount.
  const wasQueuePageRef = useRef(onQueuePageNow)
  useEffect(() => {
    if (onQueuePageNow && !wasQueuePageRef.current && session) void refreshStats()
    wasQueuePageRef.current = onQueuePageNow
  }, [onQueuePageNow, session, refreshStats])

  // The stuck-tier diagnostic: requested when the Pipeline dialog opens, and
  // again on the top-bar Refresh while it stays open — never on the 30-second
  // timer, which is why this is keyed on `manualNonce` rather than `refresh`.
  // A rejection clears it rather than leaving a stale warning.
  useEffect(() => {
    if (!pipelineOpen) return
    let cancelled = false
    loadStuckNodes()
      .then((res) => {
        if (!cancelled) setStuck(res)
      })
      .catch(() => {
        if (!cancelled) setStuck({})
      })
    return () => {
      cancelled = true
    }
  }, [pipelineOpen, manualNonce])

  // Remember the last timeline node the user worked, so Processing returns to it.
  useEffect(() => {
    if (
      selected !== null &&
      selected !== 'dashboard' &&
      selected !== 'training' &&
      selected !== 'rules' &&
      selected !== 'assignments' &&
      selected !== 'errors' &&
      selected !== 'notifications'
    )
      lastNodeRef.current = selected
  }, [selected])

  // ---- render ----
  if (sessionError) {
    return (
      <div className={s.errorPage}>
        <MessageBar intent="error"><MessageBarBody>Could not start the app: {sessionError}</MessageBarBody></MessageBar>
      </div>
    )
  }
  if (!session) {
    return <div className={s.loading}><Spinner label="Signing in…" /></div>
  }

  const isAdmin = hasRole(session, 'Admin')
  // References tab: one gate, `canOpenReferences` (identity.ts) — TRUE when
  // the session holds at least one References tab role. Owner item 29,
  // 2026-08-28: "A user should only see the References tab at all if he has a
  // role within that block." The server computes the same answer
  // (`capabilities.reference_sections`) and every write route re-asks it, so
  // this only decides whether the tab is OFFERED.
  const referenceTabs = visibleReferenceTabs(session)
  const isReferences = canOpenReferences(session)
  // Rules tab: the same shape, from `capabilities.rules_sections`.
  const ruleTabs = visibleRuleTabs(session)
  const isRules = ruleTabs.length > 0
  // Assignments tab: one gate, `canOpenAssignments` (identity.ts), used here
  // for the sidebar entry, in `landingPage` for the startup screen, and again as
  // the render guard below — the same question asked once rather than three
  // sites each risking a partial version of the AND it depends on.
  const canAssign = canOpenAssignments(session)
  // Analytics tab: same pattern, `canOpenAnalytics` (identity.ts) — owner item
  // 12, 2026-08-13. The tab used to be ungated; it is now an Analytics seat
  // (D-258).
  const canSeeAnalytics = canOpenAnalytics(session)
  // Errors tab (owner, 2026-09-14): the Admin role alone, `canOpenErrors`
  // (identity.ts); the two `/ops` reads behind it are `requireAdmin` too.
  const canSeeErrors = canOpenErrors(session)
  // Which Analytics sub-tabs (owner, 2026-09-10) — the same shape `ruleTabs`
  // above takes. `canSeeAnalytics` is now "at least one of these".
  const analyticsTabs = visibleAnalyticsTabs(session)
  // Document (PDF) preview, DERIVED from the profile rather than hard-coded per
  // tier — the same question `ArchivesScreen` asks. It used to be `false` on
  // Department and Director and `true` on Oversight, which was the policy until
  // 2026-08-10; owner ruling R2 (issue 2) granted the pane to all three tiers and
  // `canPreviewPdf` in authority.ts was widened to match — but these three
  // literals were not, so the capability existed everywhere except the screen
  // that shows it. Deriving it means the next policy change moves one function,
  // not three call sites.
  const canSeePdf = canPreviewPdf(session)
  const hasAnyLit = litRoles.size > 0
  // Does this session have ANY sidebar entry at all? Read off the same five gates the
  // sidebar is built from, so the empty state below and the sidebar beside it
  // cannot disagree about whether there is anywhere to go (QA wave B2, B2-03).
  const hasAnySection =
    canSeeAnalytics || isReferences || isRules || canAssign || canSeeErrors
  // Which workspaces the user can act in; drives the workspace entry.
  const wss = availableWorkspaces(session)
  // Switch workspace and land on that workspace's first actionable node. The
  // filter clears across the switch: the vendor option sets would not overlap,
  // so a value carried over could silently empty every queue.
  const switchWorkspace = (ws: Workspace) => {
    setWorkspace(ws)
    setFilter(emptyFilter)
    const lit = litRolesFor(scopeSession(session, ws))
    setSelected(firstLitNode(lit) ?? null)
  }
  // The sidebar entry that reflects the current view: a page key (assignments/
  // analytics/training/rules) wins; otherwise any pipeline node maps to its
  // workspace entry.
  const navValue: NavValue =
    selected === 'assignments'
      ? 'assignments'
      : selected === 'training'
        ? 'training'
        : selected === 'rules'
          ? 'rules'
          : selected === 'errors'
            ? 'errors'
            : selected === 'notifications'
              ? 'notifications'
              : selected === 'dashboard'
              ? 'analytics'
              : 'invoices'
  // True on a pipeline queue — i.e. not one of the off-pipeline pages. Drives
  // which header tools are shown.
  const onQueuePage = onQueuePageNow
  // "Go To <Step>", for a read-only invoice opened off the Dashboard (owner,
  // 2026-09-14). Answers with the queue screen this invoice is actually sitting
  // in, and `null` when there is nothing to offer: a status that matches no
  // node, a node whose role the viewer does not hold, or — since 2026-09-30
  // (owner, D-252: "accounting also for the invoice's company") — a step whose
  // queue would not list this invoice for this viewer. `goToNodeFor`
  // (lib/identity.ts) asks both, the second through `filterDecidable`, the
  // same filter the step screens and the queue badges run, with the same
  // workspace-scoped session the badges use.
  //
  // A PLAIN FUNCTION, not a `useCallback`: this sits below the shell's early
  // returns (no session yet, session failed), so a hook here would be called
  // conditionally. Nothing memoizes on its identity — the modal calls it during
  // its own render — so there is nothing to gain from stabilizing it anyway.
  const resolveStep = (inv: Invoice) => {
    const node = scoped ? goToNodeFor(scoped, litRoles, inv) : null
    if (!node) return null
    return { label: `Go To ${node.label}`, onGo: () => setSelected(node.key) }
  }

  const onNav = (v: NavValue) => {
    if (v === 'assignments') setSelected('assignments')
    else if (v === 'training') setSelected('training')
    else if (v === 'rules') setSelected('rules')
    else if (v === 'analytics') setSelected('dashboard')
    else if (v === 'errors') setSelected('errors')
    else if (v === 'notifications') setSelected('notifications')
    else switchWorkspace('invoice')
  }

  // The Invoices section's page tabs: the pipeline steps this person holds, in
  // pipeline order — the same lit set the Pipeline dialog colours blue, so the
  // tabs and the dialog can never disagree about where someone may go.
  const stepTabs = TIMELINE_NODES.filter((n) => litRoles.has(n.role)).map((n) => ({ value: n.key, label: n.label }))

  // The sidebar, built from one list so a second workspace adds a group here
  // rather than a second hand-written block. Each gate is the one the tab
  // strip used before the move (owner, 2026-09-28).
  const navItems: { value: NavValue; label: string; icon: ReactElement; show: boolean }[] = [
    // The workspace's own pipeline. Rendered from the workspace list, so a
    // second document class needs no change to this entry.
    ...wss.map((ws) => ({ value: 'invoices' as const, label: WORKSPACE_LABEL[ws], icon: <ReceiptRegular />, show: true })),
    // Notifications (owner, 2026-10-07 — D-310), under Invoices: every notification sent to
    // the signed-in person. Anyone may be listed for one, so everyone sees it.
    { value: 'notifications', label: 'Notifications', icon: <AlertRegular />, show: true },
    // An Analytics seat for at least one company (D-258).
    { value: 'analytics', label: 'Analytics', icon: <BoardRegular />, show: canSeeAnalytics },
    // Labelled "References": the training passes this screen was built for are
    // retired, and all it does now is maintain the organization's own lists.
    // The role token stays `training` — the label is the part that changed.
    // A book with a small record-stack reads as reference data (item 8,
    // 2026-08-12); the lightbulb it replaced read as "training/ideas".
    { value: 'training', label: 'References', icon: <BookDatabaseRegular />, show: isReferences },
    // Rules (owner item 10a, 2026-08-13): master data that decides invoice
    // ROUTING, split out of References, gated on its per-tab roles.
    { value: 'rules', label: 'Rules', icon: <SettingsRegular />, show: isRules },
    { value: 'assignments', label: 'Assignments', icon: <PeopleTeamRegular />, show: canAssign },
    // Errors (owner, 2026-09-14): application failures read live from the
    // logs, and the deploy reports. Admin only — see canSeeErrors.
    { value: 'errors', label: 'Errors', icon: <ErrorCircleRegular />, show: canSeeErrors },
  ]

  return (
    <div className={s.shell}>
      {/* BOTTOM-END, not top-end. At `top-end` a toast lands on top of the
          header's tool cluster — measured at a 1343px viewport, the toast
          occupied x1016-1308 / y32-78 while the six toolbar buttons occupied
          x1000-1284 / y34-66, covering every one of them. `.fui-Toaster` is
          `pointer-events: none` but each `.fui-ToastContainer` is
          `pointer-events: all`, so the clicks did not fall through:
          `elementFromPoint` over the Pipeline button returned the toast's own
          title. That is the worst button to lose, because the Pipeline dialog
          is the only map of the queue screens (`setSelected` below) — and
          the toast fires exactly when a reviewer has just finished with a queue
          and wants to move on. The bottom corner is empty on every screen. */}
      <Toaster toasterId={TOASTER_ID} position="bottom-end" />
      {/* The Upload progress panel (owner, 2026-09-03), mounted HERE rather
          than in the Upload dialog: an uploader presses Send and closes that
          window immediately, and the tracking used to die with it. It reads
          its list from `lib/uploadTracker` module state, renders nothing
          while that list is empty, and offsets itself above the Toaster's
          own bottom-right corner so the two never overlap. */}
      <UploadProgressToast />
      <div className={s.topbar} id={TOPBAR_ID}>
        <div className={s.navZone}>
          {/* The sidebar's toggle. It stays in the same place whether the
              sidebar is open or not, so there is one button to look for. */}
          <Tooltip content={navOpen ? 'Hide Navigation' : 'Show Navigation'} relationship="label">
            <Hamburger className={s.hamburger} aria-expanded={navOpen} onClick={toggleNav} />
          </Tooltip>
          <span className={s.wordmark}>Accounts Payable</span>
          <BrandMark className={s.brandLogo} />
          {/* The page tabs of whichever section is open. Each screen draws its
              own tabs into this slot (`PageTabs.tsx`); on Invoices they are the
              pipeline steps, rendered below with the queue screens. */}
          <PageTabsHost />
        </div>
        {/* Same-screen tools: compact icon buttons with tooltips. Reminders,
            Filter, Refresh and Pipeline all act on a queue, so they are hidden on
            the off-pipeline pages (Analytics / References / Rules / Assignments)
            where there is no queue to filter, refresh or navigate away from
            (user note 2026-08-03). **Upload is hidden there too since
            2026-09-11** (owner): it stayed everywhere on the reasoning that a
            document can arrive while you are anywhere in the app, which is true
            and is not the point — the rest of this cluster already hides itself
            off-pipeline, so Upload was the one control left behind, and a lone
            button in an otherwise empty corner reads as unrelated to the page
            under it. The user guide is the deliberate exception and stays
            everywhere, because it covers every page rather than the queues.
            The order (owner, 2026-09-29, D-202): User Guide first on every
            page, then the page's own tools, then Reminders, Filter, Refresh and
            Pipeline, and Upload last. Pipeline was last until that date. The
            JSX order below must match this comment. */}
        <div className={s.actions}>
          {/* THE USER GUIDE LEADS EVERY PAGE'S CLUSTER (owner, 2026-09-29, D-202:
              "make the information icon the left-most button in the row of
              action buttons in the top-right corner"). */}
          {/* User guide: a printable PDF, opened in a new tab so it reads beside
              the work rather than replacing it. It is the one control in this
              cluster that shows on every page. Since D-389 (owner, 2026-10-20)
              it opens the page's own one-pager on each Invoices step,
              Notifications and Analytics, and the full guide on References,
              Rules, Assignments and Errors — `guideUrlFor` in `lib/guides.ts`. */}
          <Tooltip content="User Guide" relationship="label">
            <Button
              appearance="secondary"
              icon={<InfoRegular />}
              aria-label="User Guide"
              onClick={() => window.open(guideUrlFor(selected), '_blank', 'noopener,noreferrer')}
            />
          </Tooltip>
          {/* The off-pipeline pages' own tools land HERE (owner, 2026-09-14),
              portalled in by whichever manager is showing — Filter, Import,
              Add Reference, Add Rule. They follow the User Guide, which leads
              every page's cluster since 2026-09-29 (D-202). `display: contents`, so the host adds no box of its own
              and the buttons are laid out by `actions` exactly as its own
              children are. See `components/ScreenActions.tsx`. */}
          <ScreenActionsHost />
          {onQueuePage && (
            <>
              {/* Reminders: self-service email digests. Shown to anyone with a
                  pipeline role (i.e. steps to be reminded about). */}
              {wss.length > 0 && (
                <Tooltip content="Custom Notifications" relationship="label">
                  <Button appearance={remindersOpen ? 'primary' : 'secondary'} icon={<MailRegular />} onClick={() => setRemindersOpen(true)} />
                </Tooltip>
              )}
              {/* iconOnly: every other control in this cluster is an icon with a
                  tooltip; the active-group count already surfaces in the tooltip
                  and the button's own color, so a visible "Filter" label is
                  redundant here and must not come back (owner feedback,
                  2026-08-14). */}
              <FilterButton
                iconOnly
                companies={filterCompanies}
                vendors={filterVendors}
                value={filter}
                onChange={setFilter}
              />
              <Tooltip content="Refresh" relationship="label">
                <Button appearance="secondary" icon={<ArrowClockwiseRegular />} onClick={() => refreshAll()} />
              </Tooltip>
              <Tooltip content="Pipeline" relationship="label">
                {/* THE PIPELINE BUTTON IS ALSO A SILENT REFRESH (owner, 2026-09-15
                    and 2026-09-25): the counts in the dialog AND the gallery
                    behind it, together, so the two never disagree. */}
                <Button
                  appearance={pipelineOpen ? 'primary' : 'secondary'}
                  icon={<TimelineRegular />}
                  onClick={() => {
                    refreshAll({ silent: true })
                    setPipelineOpen(true)
                  }}
                />
              </Tooltip>
              {/* UPLOAD IS THE RIGHT-HAND END on the Invoices pages (owner,
                  2026-09-29, D-202: "For Invoices, make Upload the
                  furthest-right"), after Pipeline. */}
              <Tooltip content="Upload" relationship="label">
                {/* DISABLED for someone who may not upload in the app (owner,
                    2026-10-22 — D-394): no standing, or an Uploader rule that
                    says in-app uploads are not allowed. The server refuses
                    them anyway; this saves them the try. */}
                <Button
                  appearance={uploadOpen ? 'primary' : 'secondary'}
                  icon={<ArrowUploadRegular />}
                  disabledFocusable={!session.canUpload}
                  onClick={() => setUploadOpen(true)}
                />
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <div className={s.body}>
        {/* The sections, grouped under the workspace's heading. Only the
            sections this person may open are listed; the API still enforces
            every read independently of what is shown here. */}
        <div className={mergeClasses(s.sidebarFrame, !navOpen && s.sidebarFrameClosed)}>
        <NavDrawer
          type="inline"
          open
          className={s.sidebar}
          selectedValue={navValue}
          onNavItemSelect={(_, d) => onNav(d.value as NavValue)}
          aria-label="Sections"
        >
          <NavDrawerBody>
            {wss.map((ws) => (
              <NavSectionHeader key={ws}>{WORKSPACE_NAV_HEADING[ws]}</NavSectionHeader>
            ))}
            {navItems
              .filter((item) => item.show)
              .map((item) => (
                <NavItem key={item.value} value={item.value} icon={{ children: item.icon, className: s.navIcon }}>
                  {item.label}
                </NavItem>
              ))}
          </NavDrawerBody>
        </NavDrawer>
        </div>

      {/* The ids anchor the detail modal's overlay measurements (lib/overlay.ts). */}
      <div className={s.page} id={PAGE_ID}>
        {onQueuePage && hasAnyLit && (
          <PageTabs
            tabs={stepTabs}
            selectedValue={selected as NodeKey | null}
            onSelect={(k) => setSelected(k)}
            ariaLabel="Pipeline steps"
          />
        )}
        {selected === 'assignments' ? (
          // Reachable today only via the sidebar (gated on `canAssign`) or
          // `landingPage` (gated on the same `canOpenAssignments`) — this is
          // a third, explicit check on the same question, so a future path
          // that sets `selected` to 'assignments' some other way cannot
          // render the screen unguarded.
          canAssign ? <AssignmentsScreen session={session} /> : null
        ) : selected === 'notifications' ? (
          <NotificationsScreen />
        ) : selected === 'errors' ? (
          // Same third-explicit-check pattern as Assignments above (owner,
          // 2026-09-14): the tab is gated on `canSeeErrors`, and so is the render.
          canSeeErrors ? <ErrorsScreen /> : null
        ) : selected === 'training' ? (
          // `fleetOnly` asks "is Vehicles the ONLY tab this person has", from
          // the visible tabs.
          <TrainingScreen
            fleetOnly={referenceTabs.length === 1 && referenceTabs[0] === 'vehicles'}
            sections={referenceTabs}
          />
        ) : selected === 'rules' ? (
          // Same third-explicit-check pattern as Assignments above: reachable
          // via the sidebar (gated on `isRules`) or landed on directly only by
          // this very state, but the render guard is repeated here on purpose.
          isRules ? <RulesScreen sections={ruleTabs} /> : null
        ) : selected === 'dashboard' ? (
          // Owner item 12, 2026-08-13: gated on `canSeeAnalytics`
          // (`canOpenAnalytics`), same pattern as Assignments and Rules above.
          canSeeAnalytics ? (
            <AnalyticsScreen
              session={session}
              refreshNonce={manualNonce}
              sections={analyticsTabs}
              resolveStep={resolveStep}
            />
          ) : null
                ) : !hasAnyLit || selected === null ? (
          <div className={s.noRole}>
            {/* TWO MESSAGES, because they are two different situations (QA wave
                B2, B2-03). The first is somebody who works a section of this app
                but holds no pipeline step — they have somewhere to be, and the
                sentence is about the step they are missing. The second is
                somebody who has been given NOTHING yet, which is an ordinary
                state on the day an account is created and used to render as a
                white page with no explanation at all. Every other empty state
                here names its own cause; so does this one. */}
            <Body1>
              {hasAnySection ? (
                <>
                  You do not hold any invoice processing step yet.
                  {isAdmin ? ' Use Assignments to grant yourself or someone else a step.' : ' Ask an administrator to assign you one.'}
                </>
              ) : (
                <>
                  Nothing is open to you yet. Your account holds no role and no company
                  assignment, so there is no screen here to show you. Ask an administrator
                  to give you one.
                </>
              )}
            </Body1>
          </div>
        ) : selected === 'import-control' ? (
          <ImportControlScreen session={session} refresh={refresh} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'controller' ? (
          // Controller Approval (owner, 2026-10-07 — D-303): the alternative
          // to Manager Approval for an invoice a Controller routing rule
          // matched at Import Control's release. The one approval step that
          // may still change BOTH halves of a line's code — they lock once it
          // approves (D-305) — so it is also held to the coding gate.
          <ApprovalScreen
            key="approval-controller"
            session={session}
            refresh={refresh}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              subtitle:
                'For each line item, confirm its Account and Department, and then approve or deny the invoice.',
              queueStatus: InvoiceStatus.PendingController,
              step: DecisionStep.Controller,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              editAccounts: true,
              editDepartments: true,
              requireCodedLineItems: true,
            }}
          />
        ) : selected === 'department' ? (
          // The linear approval tiers (planning §3): Controller or Manager →
          // Director → Oversight. Each is a keyed ApprovalScreen instance so React doesn't
          // reuse one across a tier switch (state — holds/selection/open — would
          // leak). Every tier shows the PDF pane when the profile allows it
          // (`canSeePdf`, above): owner ruling R2 made document access a
          // capability of holding any approval seat, not a property of the tier,
          // and `docs/security.md` §5 owns the policy and its accepted exposure.
          // THE GL CODE IS LOCKED from here on (owner, 2026-10-07 — D-309): "For
          // the Manager path, Account and Department should now be accessible
          // and required at Import Control and Export Control, but both fields
          // should be locked elsewhere. Manager Approval is no longer the step
          // for assigning the Department; that is to happen now at Import
          // Control." Until then the Department stayed open here (D-305).
          <ApprovalScreen
            key="approval-department"
            session={session}
            refresh={refresh}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              // "Department" is the coding column's own name now (owner item
              // 8's rename B — "Who Is It For?" → "Department"), so this
              // names the field explicitly rather than the retired step name
              // it used to echo (E10, docs/decisions.md D-43).
              // The owner's sentence (2026-10-07): the Department is set at
              // Import Control since D-309, so this step no longer asks for it.
              subtitle: 'Approve or deny each invoice.',
              queueStatus: InvoiceStatus.PendingDepartment,
              step: DecisionStep.Department,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              // Both halves locked past Import Control (D-309).
              editAccounts: false,
              editDepartments: false,
              // D-9: Approve is refused — and so not offered — while any active
              // line item is missing either half of its GL code. Set on all
              // three tiers, matching the server's own `CODING_GATED_STEPS`.
              requireCodedLineItems: true,
            }}
          />
        ) : selected === 'director' ? (
          <ApprovalScreen
            key="approval-director"
            session={session}
            refresh={refresh}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              subtitle: 'Approve or deny each invoice.',
              queueStatus: InvoiceStatus.PendingDirector,
              step: DecisionStep.Director,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              // Both halves locked past Import Control (D-309).
              editAccounts: false,
              editDepartments: false,
              // D-9: Approve is refused — and so not offered — while any active
              // line item is missing either half of its GL code. Set on all
              // three tiers, matching the server's own `CODING_GATED_STEPS`.
              requireCodedLineItems: true,
            }}
          />
        ) : selected === 'oversight' ? (
          <ApprovalScreen
            key="approval-oversight"
            session={session}
            refresh={refresh}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              subtitle: 'Approve or deny each invoice.',
              queueStatus: InvoiceStatus.PendingOversight,
              step: DecisionStep.Oversight,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              // Both halves locked past Import Control (D-309).
              editAccounts: false,
              editDepartments: false,
              // D-9: Approve is refused — and so not offered — while any active
              // line item is missing either half of its GL code. Set on all
              // three tiers, matching the server's own `CODING_GATED_STEPS`.
              requireCodedLineItems: true,
            }}
          />
        ) : selected === 'export-control' ? (
          <ExportControlScreen session={session} refresh={refresh} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'closing-confirmation' ? (
          <ClosingConfirmationScreen session={session} refresh={refresh} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'denial-review' ? (
          <DenialReviewScreen session={session} refresh={refresh} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'archives' ? (
          <ArchivesScreen session={session} refresh={refresh} filter={filter} />
        ) : null}

        <div className={s.versionLine}>v{APP_VERSION}</div>
      </div>
      </div>

      <AppDialog open={pipelineOpen} onOpenChange={(_, d) => setPipelineOpen(d.open)}>
        {/* Wide enough for the main-path timeline (Import Control → Controller
            Approval over Manager Approval → Director Approval → Oversight
            Approval → Export Control → Closing Confirmation) plus the fork +
            branch + archive columns, with no horizontal scrollbar. The
            timeline's min-content is 1048px since Controller Approval joined
            (D-303) (`lib/timelineLayout.ts`'s `timelineMinWidth`); this surface
            leaves it 1182px after chrome — `pipeline-dialog-width.test.ts`
            holds the two together. */}
        <DialogSurface style={{ maxWidth: PIPELINE_DIALOG_MAX_W }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={() => setPipelineOpen(false)}
                />
              }
            >
              Pipeline
            </DialogTitle>
            <DialogContent>
              <Body1 className={s.pipelineSub}>
                Navigate to a particular step in the invoice processing pipeline. Steps to which you have
                access are blue; steps to which you do not have access are gray.
              </Body1>
              <div className={s.pipelineBody}>
                <Timeline
                  litRoles={litRoles}
                  selected={
                    selected === 'dashboard' ||
                    selected === 'training' ||
                    selected === 'rules' ||
                    selected === 'assignments' ||
                    selected === 'errors' ||
                    selected === 'notifications'
                      ? null
                      : selected
                  }
                  counts={counts}
                  stuck={stuck}
                  onSelect={(k) => {
                    setSelected(k)
                    setPipelineOpen(false)
                  }}
                />
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </AppDialog>

      {/* The one draggable dialog in the app (owner item 2, 2026-08-31), and
          MODAL like every other one (owner, 2026-09-30; D-244): "I want to keep
          it movable while still making it the only in-app on-screen focus." It
          was non-modal until then, so the page behind it took clicks and Tab.
          Now the page is dimmed and blocked, focus stays inside, and the drag
          still works: the surface sits above the backdrop, and the title keeps
          the pointer captured for the whole drag.
          A BACKDROP CLICK DOES NOT CLOSE IT, unlike the Pipeline pop-up. Closing
          empties the staged files and the Company, and a drag released just
          past the surface's edge must not throw them away. Escape and the
          title's Close button close it, as they close Pipeline.
          `dragUpload.backdropProps` stops a file dropped on the dimmed page
          from being opened by the browser in place of the app.
          `dragUpload.handleProps` goes on the TITLE and nowhere else: the drop
          zone lives under `DialogContent`, and a pointer handler over that
          subtree breaks the file drop and the click-to-browse. */}
      <AppDialog open={uploadOpen} onOpenChange={(_, d) => { if (d.type !== 'backdropClick') setUploadOpen(d.open) }}>
        <DialogSurface {...dragUpload.surfaceProps} backdrop={dragUpload.backdropProps} style={{ maxWidth: 560, ...dragUpload.surfaceStyle }}>
          <DialogBody>
            <DialogTitle
              {...dragUpload.handleProps}
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={() => setUploadOpen(false)}
                />
              }
            >
              Upload
            </DialogTitle>
            <DialogContent>
              {/* Submit closes the pop-up (owner, D-205); the send carries on
                  in the progress toast. */}
              <UploadScreen open={uploadOpen} onSubmitted={() => setUploadOpen(false)} onClose={() => setUploadOpen(false)} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </AppDialog>

      <AppDialog open={remindersOpen} onOpenChange={(_, d) => setRemindersOpen(d.open)}>
        {/* 860px since the list became a table (owner, 2026-10-14); its four
            columns did not fit the 620px the cards did. */}
        <DialogSurface style={{ maxWidth: 860 }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={() => setRemindersOpen(false)}
                />
              }
            >
              {remindersTitle}
            </DialogTitle>
            <DialogContent>
              <RemindersScreen session={session} onTitleChange={setRemindersTitle} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </AppDialog>
    </div>
  )
}
